import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';
import '../messages/ThreadQuill.css';
import { openOrSendFirstMessage } from '../players/store/actions';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const replyQuillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const replyQuillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const isQuillEmpty = val => !val || val.replace(/<[^>]*>/g, '').trim() === '';

const AssistantMyPlayers = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    axios
      .get(`${url}/checklist/my-assignments`, { headers: getAuthHeader() })
      .then(res => setPlayers(res.data.players || []))
      .catch(() => setError('Nie udało się załadować listy zawodników.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return players;
    const q = search.toLowerCase();
    return players.filter(p => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [players, search]);

  const openPlayer = (id, options = {}) => {
    const params = new URLSearchParams();
    if (options.tab) params.set('tab', options.tab);
    const query = params.toString();
    history.push(`/admin/my-players/${id}${query ? `?${query}` : ''}`);
  };

  const openMessageModal = player => {
    setSelectedPlayer(player);
    setMessageText('');
    setShowMessageModal(true);
  };

  const closeMessageModal = () => {
    if (sendingMessage) return;
    setShowMessageModal(false);
    setSelectedPlayer(null);
    setMessageText('');
  };

  const sendMessageToPlayer = async () => {
    const selectedPlayerId = selectedPlayer?._id || selectedPlayer?.id;
    if (!selectedPlayerId || isQuillEmpty(messageText) || sendingMessage) return;
    setSendingMessage(true);
    const result = await dispatch(
      openOrSendFirstMessage({ playerId: selectedPlayerId, text: messageText })
    );
    setSendingMessage(false);
    if (result) closeMessageModal();
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '900px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
            <i
              className="fas fa-user-friends"
              style={{ marginRight: '10px', color: '#2e496b' }}
            />
            Moi zawodnicy
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
            {players.length > 0
              ? `${players.length} przypisanych zawodników`
              : 'Brak przypisanych zawodników'}
          </p>
        </div>

        {players.length > 4 && (
          <div style={{ position: 'relative' }}>
            <i
              className="fas fa-search"
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                fontSize: '13px',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Szukaj zawodnika..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '7px 12px 7px 32px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '14px',
                width: '220px',
              }}
            />
          </div>
        )}
      </div>

      {/* Empty state */}
      {players.length === 0 && (
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '56px',
            textAlign: 'center',
          }}
        >
          <i
            className="fas fa-user-friends"
            style={{
              fontSize: '36px',
              color: '#94a3b8',
              marginBottom: '14px',
              display: 'block',
            }}
          />
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Nie masz jeszcze przypisanych zawodników.
          </p>
        </div>
      )}

      {/* Player grid */}
      {filtered.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '18px',
          }}
        >
          {filtered.map(player => {
            const id = player._id || player.id;
            const total = player.total || 0;
            const done = player.done || 0;
            const percent = total > 0 ? Math.round((done / total) * 100) : 0;

            return (
              <div
                key={id}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
                onClick={() => openPlayer(id, { tab: 'checklist' })}
                role="button"
                tabIndex={0}
                onKeyPress={e => e.key === 'Enter' && openPlayer(id, { tab: 'checklist' })}
              >
                {/* Avatar / photo */}
                <div
                  style={{
                    background: player.imageBase64
                      ? '#1e293b'
                      : 'linear-gradient(135deg, #2e496b 0%, #3b82f6 100%)',
                    height: '130px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {player.imageBase64 ? (
                    <img
                      src={player.imageBase64}
                      alt={`${player.firstName} ${player.lastName}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.parentNode.style.background =
                          'linear-gradient(135deg, #2e496b 0%, #3b82f6 100%)';
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: 700,
                        border: '2px solid rgba(255,255,255,0.3)',
                        userSelect: 'none',
                      }}
                    >
                      {(player.firstName || '?').charAt(0).toUpperCase()}
                      {(player.lastName || '').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: '18px' }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '15px',
                      color: '#1e293b',
                      marginBottom: '2px',
                    }}
                  >
                    {player.firstName} {player.lastName}
                  </div>
                  {player.sport && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                      <i className="fas fa-running" style={{ marginRight: '5px', color: '#94a3b8' }} />
                      {player.sport}
                    </div>
                  )}

                  {/* Progress bar */}
                  {total > 0 && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '11px',
                          color: '#64748b',
                          marginBottom: '4px',
                        }}
                      >
                        <span>Postęp checklisty</span>
                        <span style={{ fontWeight: 600, color: percent === 100 ? '#16a34a' : '#1e293b' }}>
                          {done}/{total} ({percent}%)
                        </span>
                      </div>
                      <div
                        style={{
                          height: '6px',
                          background: '#f1f5f9',
                          borderRadius: '999px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${percent}%`,
                            background:
                              percent === 100
                                ? '#16a34a'
                                : percent >= 50
                                ? '#3b82f6'
                                : '#f59e0b',
                            borderRadius: '999px',
                            transition: 'width 0.3s',
                          }}
                        />
                      </div>
                    </>
                  )}

                  {total === 0 && (
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      Brak przypisanych zadań
                    </div>
                  )}

                  {/* Quick links */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '14px',
                      padding: '6px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openPlayer(id, { tab: 'checklist' });
                      }}
                      style={{
                        flex: 1,
                        padding: '9px 10px',
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        border: '1px solid #bfdbfe',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fas fa-tasks" style={{ marginRight: '4px' }} />
                      Checklist
                    </button>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openPlayer(id);
                      }}
                      style={{
                        flex: 1,
                        padding: '9px 10px',
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fas fa-user" style={{ marginRight: '4px' }} />
                      Profil
                    </button>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openMessageModal(player);
                      }}
                      style={{
                        flex: 1,
                        padding: '9px 10px',
                        background: '#ecfdf5',
                        color: '#047857',
                        border: '1px solid #a7f3d0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fas fa-paper-plane" style={{ marginRight: '4px' }} />
                      Wyślij wiadomość
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showMessageModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
          }}
          onClick={closeMessageModal}
          role="button"
          tabIndex={0}
          onKeyPress={e => e.key === 'Enter' && closeMessageModal()}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '720px',
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 45px rgba(2, 6, 23, 0.25)',
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>
                  Wyślij wiadomość
                </h3>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                  {selectedPlayer?.firstName} {selectedPlayer?.lastName}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMessageModal}
                disabled={sendingMessage}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#64748b',
                  fontSize: '18px',
                  cursor: sendingMessage ? 'not-allowed' : 'pointer',
                }}
                aria-label="Zamknij"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div style={{ padding: '18px 20px 20px' }}>
              <div className="thread-quill thread-quill--contact" style={{ marginBottom: '14px' }}>
                <ReactQuill
                  value={messageText}
                  onChange={setMessageText}
                  modules={replyQuillModules}
                  formats={replyQuillFormats}
                  theme="snow"
                  placeholder="Wpisz wiadomość..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={closeMessageModal}
                  disabled={sendingMessage}
                  style={{
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    borderRadius: '8px',
                    padding: '9px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: sendingMessage ? 'not-allowed' : 'pointer',
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={sendMessageToPlayer}
                  disabled={isQuillEmpty(messageText) || sendingMessage}
                  style={{
                    border: '1px solid #0f766e',
                    background: '#14b8a6',
                    color: '#fff',
                    borderRadius: '8px',
                    padding: '9px 14px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: isQuillEmpty(messageText) || sendingMessage ? 'not-allowed' : 'pointer',
                    opacity: isQuillEmpty(messageText) || sendingMessage ? 0.7 : 1,
                  }}
                >
                  {sendingMessage ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No search results */}
      {filtered.length === 0 && players.length > 0 && (
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '14px',
          }}
        >
          Brak wyników dla „{search}"
        </div>
      )}
    </div>
  );
};

export default AssistantMyPlayers;
